<script setup lang="ts">
import { Portal } from 'primevue';
import type { TransitionProps } from 'vue';
import { lockBodyScroll, unlockBodyScroll } from '~/utilities/ui-utilities';

interface IDrawerProps {
  position?: 'left' | 'right' | 'top' | 'bottom';
  dismissable?: boolean;
  closeButton?: boolean;
  unstyled?: boolean;
  customTransitionClass?: Partial<TransitionProps>;
  customContainerClass?: string;
  zIndex?: string;
  overRideContentClass?: boolean;
}

const props = withDefaults(defineProps<IDrawerProps>(), {
  position: 'left',
  dismissable: true,
  unstyled: false,
  customContainerClass: '',
  customTransitionClass: null,
  overRideContentClass: false,
  zIndex: 'z-40',
});

const emits = defineEmits<{
  (e: 'update:visible'): void;
  (e: 'open'): void;
  (e: 'close'): void;
}>();
const attrs = useAttrs();
const visible = defineModel('visible', { type: Boolean });

const transitionClass = computed<Partial<TransitionProps>>(() => {
  if (props?.unstyled) return props?.customTransitionClass;

  const positionalClass = ref<Partial<TransitionProps>>();
  switch (props?.position) {
    case 'left':
      positionalClass.value = {
        enterFromClass: '-translate-x-full',
        enterToClass: 'translate-x-0',
        leaveFromClass: 'translate-x-0',
        leaveToClass: '-translate-x-full',
      };
      break;
    case 'right':
      positionalClass.value = {
        enterFromClass: 'translate-x-full',
        enterToClass: 'translate-x-0',
        leaveFromClass: 'translate-x-0',
        leaveToClass: 'translate-x-full',
      };
      break;
    case 'bottom':
      positionalClass.value = {
        enterFromClass: 'translate-y-full',
        enterToClass: 'translate-y-0',
        leaveFromClass: 'translate-y-0',
        leaveToClass: 'translate-y-full',
      };
      break;
    case 'top':
      positionalClass.value = {
        enterFromClass: '-translate-y-full',
        enterToClass: 'translate-y-0',
        leaveFromClass: 'translate-y-0',
        leaveToClass: '-translate-y-full',
      };
      break;
    default:
      break;
  }

  return { ...positionalClass.value, ...props?.customTransitionClass };
});

const containerClass = computed<string>(() => {
  if (props?.unstyled || 'force-custom-container-class' in attrs)
    return props?.customContainerClass;
  const positionalClass = ref<string>();
  switch (props?.position) {
    case 'left':
      positionalClass.value = 'h-full w-full md:w-[360px] top-0 left-0';
      break;
    case 'right':
      positionalClass.value = 'h-full w-full md:w-[360px] top-0 right-0';
      break;
    case 'bottom':
      positionalClass.value = 'h-auto w-full bottom-0 left-0';
      break;
    case 'top':
      positionalClass.value = 'h-auto w-full top-0 left-0';
      break;
    default:
      positionalClass.value = '';
      break;
  }
  return positionalClass.value.concat(' ', props?.customContainerClass);
});

function closeDrawer() {
  if (props.dismissable) emits('update:visible');
}

function onEnter() {
  lockBodyScroll();
  emits('open');
}

function onLeave() {
  unlockBodyScroll();
  emits('close');
}
</script>

<template>
  <slot name="activator" />
  <Portal>
    <div
      v-if="visible"
      :class="[
        'fixed h-full w-full top-0 left-0 flex backdrop-blur-sm bg-dark-900/50',
        zIndex,
      ]"
      @click="closeDrawer"
    />
    <Transition
      name="drawer-transition"
      enter-active-class="transition-all duration-[400ms] ease-in-out"
      leave-active-class="transition-all duration-[400ms] ease-in-out"
      v-bind="transitionClass"
      @enter="onEnter"
      @leave="onLeave"
    >
      <div
        v-if="visible"
        :class="[
          'fixed flex flex-col border-none shadow-lg pointer-events-auto',
          zIndex,
          containerClass,
        ]"
      >
        <!-- Header -->
        <div
          v-if="$slots.header"
          class="flex items-center justify-between shrink-0 overflow-hidden"
        >
          <slot name="header" />
        </div>
        <!-- Default/Content -->
        <div
          :class="{
            'pt-0 h-full w-full grow overflow-y-auto flex flex-col justify-between':
              !props.overRideContentClass,
          }"
        >
          <slot />
        </div>
        <slot name="footer" />
      </div>
    </Transition>
  </Portal>
</template>
